// client/src/components/BidEstimation/VendorQuotes/CreateCatalogItemDrawer.js
//
// Slide-in drawer for creating a new Material Catalog item from an unmatched
// vendor quote/invoice line. Replaces the old blind `handleCreateNew` that
// POSTed only description + a placeholder category.
//
// MOCKUP STATE: the universal fields (name, category, unit, cost, manufacturer,
// part number) are sent to the real /materials endpoint and persist. The
// allocation/use UoM fields are mocked — sent but currently dropped server-side
// until the materialCatalog model + endpoint are extended to store them.
import React, { useState, useEffect } from 'react'
import axios from 'axios'

// materialCatalog.model.js category enum
const CATEGORIES = [
  'irrigation', 'plant', 'aggregate', 'general',
  'lighting', 'hardscape', 'herbicide', 'fertilizer'
]

// Common units of measure — mock list for the dropdowns.
const UOMS = ['EA', 'LF', 'SF', 'CY', 'TON', 'GAL', 'BAG', 'BOX', 'CASE', 'PALLET', 'HR']

const labelStyle = {
  fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--ink-3)', display: 'block', marginBottom: 4
}

const fieldStyle = {
  width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px',
  borderRadius: 6, border: '1px solid var(--line-2)', background: 'var(--bg-panel)',
  fontSize: 13, color: 'var(--ink)', fontFamily: 'var(--f-sans, var(--font-ui))', outline: 'none'
}

function Field ({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>
        {label}
        {hint && <span style={{ marginLeft: 6, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--ink-4)' }}>{hint}</span>}
      </label>
      {children}
    </div>
  )
}

/**
 * @param {boolean} open
 * @param {object}  lineItem  - the vendor quote/invoice line that triggered this
 * @param {string}  defaultVendor - vendor name from the parent doc, prefilled
 * @param {function} onClose
 * @param {function} onCreated - (catalogItemId, catalogItemName) => void
 */
export default function CreateCatalogItemDrawer ({ open, lineItem, defaultVendor = '', onClose, onCreated }) {
  const [vendorOptions, setVendorOptions] = useState([])
  const [form, setForm] = useState({
    canonicalName: '', category: 'general', subcategory: '',
    unit: 'EA', allocationUom: 'EA', useUom: 'EA',
    costPer: '', vendor: '', sku: '', manufacturer: '', partNumber: ''
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Vendor options for the Autocomplete — fetched once on first open
  useEffect(() => {
    if (!open) return
    axios.get('/api/bid-estimation/vendors')
      .then(({ data }) => setVendorOptions(
        Array.isArray(data) ? data.map(v => v.name).filter(Boolean) : []
      ))
      .catch(() => setVendorOptions([]))
  }, [open])

  // Prefill from the line item each time the drawer opens
  useEffect(() => {
    if (open && lineItem) {
      setError(null)
      setForm({
        canonicalName: lineItem.description || '',
        category: 'general',
        subcategory: '',
        unit: lineItem.uom || 'EA',
        allocationUom: lineItem.uom || 'EA',
        useUom: lineItem.uom || 'EA',
        costPer: lineItem.unitPrice != null ? String(lineItem.unitPrice) : '',
        vendor: defaultVendor || '',
        sku: lineItem.vendorSku || '',
        manufacturer: lineItem.manufacturer || '',
        partNumber: lineItem.manufacturerSku || ''
      })
    }
  }, [open, lineItem])

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  const handleSave = async () => {
    if (!form.canonicalName.trim()) {
      setError('Item name is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      // Real fields persist; allocationUom/useUom/sku are sent but dropped
      // server-side until the model is extended (mockup phase).
      const payload = {
        canonicalName: form.canonicalName.trim(),
        category: form.category,
        subcategory: form.subcategory || undefined,
        unit: form.unit,
        allocationUom: form.allocationUom, // mock
        useUom: form.useUom,               // mock
        attributes: {
          manufacturer: form.manufacturer || undefined,
          partNumber: form.partNumber || form.sku || undefined
        },
        vendorPrices: form.costPer
          ? [{ vendor: form.vendor || 'Unknown', unitCost: Number(form.costPer) || 0 }]
          : []
      }
      const { data } = await axios.post('/api/bid-estimation/materials', payload)
      onCreated(data._id, data.canonicalName || form.canonicalName.trim())
      onClose()
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Create failed')
    }
    setSaving(false)
  }

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={saving ? undefined : onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(20,28,24,0.32)', zIndex: 1400 }}
      />
      {/* Drawer panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 420, maxWidth: '92vw',
        background: 'var(--bg-panel, #fff)', zIndex: 1401,
        boxShadow: '-8px 0 24px rgba(20,28,24,0.18)',
        display: 'flex', flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--line-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>New Catalog Item</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
              Adds to your Material Catalog
            </div>
          </div>
          <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={onClose} disabled={saving}
            style={{ fontSize: 16, width: 28, height: 28, padding: 0 }}>
            {'✕'}
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          <Field label="Item Name">
            <input style={fieldStyle} value={form.canonicalName}
              onChange={e => set('canonicalName', e.target.value)}
              placeholder="e.g. TurfMax 2k Bag" />
          </Field>

          <Field label="Category">
            <select style={{ ...fieldStyle, textTransform: 'capitalize' }} value={form.category}
              onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <Field label="Subcategory" hint="optional">
            <input style={fieldStyle} value={form.subcategory}
              onChange={e => set('subcategory', e.target.value)}
              placeholder="e.g. pre-emergent" />
          </Field>

          {/* UoM trio */}
          <div style={{ display: 'flex', gap: 10 }}>
            <Field label="Unit">
              <select style={fieldStyle} value={form.unit} onChange={e => set('unit', e.target.value)}>
                {UOMS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
            <Field label="Allocation UoM">
              <select style={fieldStyle} value={form.allocationUom} onChange={e => set('allocationUom', e.target.value)}>
                {UOMS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
            <Field label="Use UoM">
              <select style={fieldStyle} value={form.useUom} onChange={e => set('useUom', e.target.value)}>
                {UOMS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
          </div>

          {/* Pricing */}
          <div style={{ display: 'flex', gap: 10 }}>
            <Field label="Cost Per Unit">
              <input style={fieldStyle} type="number" step="0.01" value={form.costPer}
                onChange={e => set('costPer', e.target.value)} placeholder="0.00" />
            </Field>
            <Field label="Vendor" hint="for this price">
              <input style={fieldStyle} value={form.vendor} list="cci-vendor-list"
                onChange={e => set('vendor', e.target.value)}
                placeholder="Pick or type a vendor" />
              <datalist id="cci-vendor-list">
                {vendorOptions.map(v => <option key={v} value={v} />)}
              </datalist>
            </Field>
          </div>

          <Field label="SKU / Vendor Item #">
            <input style={fieldStyle} value={form.sku}
              onChange={e => set('sku', e.target.value)} placeholder="Vendor SKU" />
          </Field>

          <div style={{ display: 'flex', gap: 10 }}>
            <Field label="Manufacturer">
              <input style={fieldStyle} value={form.manufacturer}
                onChange={e => set('manufacturer', e.target.value)} />
            </Field>
            <Field label="Mfr Part #">
              <input style={fieldStyle} value={form.partNumber}
                onChange={e => set('partNumber', e.target.value)} />
            </Field>
          </div>

          {/* Category-specific section — placeholder */}
          <div style={{
            marginTop: 8, padding: '10px 12px', borderRadius: 8,
            border: '1px dashed var(--line-2)', background: 'var(--bg-panel-alt, #f5f5f5)',
            fontSize: 11.5, color: 'var(--ink-3)'
          }}>
            <b style={{ color: 'var(--ink-2)' }}>Category-specific fields — coming next.</b>
            {' '}Some categories will collect extra data here — e.g. Herbicide
            will need EPA Registration # and SDS label info.
          </div>

          {error && (
            <div style={{
              marginTop: 12, padding: '8px 12px', borderRadius: 6,
              background: 'var(--risk-tint, #ffebee)', color: 'var(--risk, #c62828)', fontSize: 12.5
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid var(--line-2)',
          display: 'flex', justifyContent: 'flex-end', gap: 8
        }}>
          <button className="hmi-btn hmi-btn-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="hmi-btn hmi-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Creating…' : 'Create Item'}
          </button>
        </div>
      </div>
    </>
  )
}
